import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Plus, Users, Edit2, Trash2, UserPlus, ArrowLeft,
  UserMinus, Loader2, AlertCircle, RefreshCw, X, FolderOpen,
  ChevronLeft, ChevronRight, UserRoundCog,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { api } from '@/lib/api';

// ── Types ──────────────────────────────────────────────────────
interface GroupSummary {
  id: string;
  name: string;
  description: string;
  membersCount: number;
  createdAt: string;
}

interface GroupMember {
  id: string;
  name: string;
  email: string;
  joinedAt: string;
}

interface GroupDetail {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  members: GroupMember[];
}

interface AvailableLearner {
  id: string;
  name: string;
  email: string;
}

const PAGE_SIZE = 10;

function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    const inner = (raw as Record<string, unknown>).data;
    if (inner && typeof inner === 'object' && 'data' in inner) return (inner as Record<string, unknown>).data as T;
    return inner as T;
  }
  return raw as T;
}

// ── Component ──────────────────────────────────────────────────
export default function GroupManagement() {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');

  // List state
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<'name' | 'membersCount' | 'createdAt'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Detail state
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupDetail, setGroupDetail] = useState<GroupDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  // Right panel — assign learners
  const [panelOpen, setPanelOpen] = useState(false);
  const [availableLearners, setAvailableLearners] = useState<AvailableLearner[]>([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  // ── Data fetching ────────────────────────────────────────────
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.get<unknown>('/institution/groups');
      const rows = unwrap<GroupSummary[]>(resp);
      setGroups(Array.isArray(rows) ? rows : []);
    } catch {
      setError(t('institution.groups.loadError'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const fetchGroupDetail = useCallback(async (groupId: string) => {
    try {
      setDetailLoading(true);
      const resp = await api.get<unknown>(`/institution/groups/${groupId}`);
      const detail = unwrap<GroupDetail>(resp);
      if (detail && typeof detail === 'object' && 'id' in detail) {
        setGroupDetail(detail);
      }
    } catch {
      setGroupDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedGroupId) fetchGroupDetail(selectedGroupId);
    else setGroupDetail(null);
  }, [selectedGroupId, fetchGroupDetail]);

  const fetchAvailableLearners = useCallback(async (groupId: string) => {
    try {
      setLearnersLoading(true);
      const resp = await api.get<unknown>(`/institution/groups/${groupId}/available-learners`);
      const rows = unwrap<AvailableLearner[]>(resp);
      setAvailableLearners(Array.isArray(rows) ? rows : []);
    } catch {
      setAvailableLearners([]);
    } finally {
      setLearnersLoading(false);
    }
  }, []);

  // ── Filtering & sorting ──────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...groups];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (g) => g.name.toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = typeof av === 'number' ? av - (bv as number) : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [groups, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filteredLearners = useMemo(() => {
    if (!learnerSearch) return availableLearners;
    const q = learnerSearch.toLowerCase();
    return availableLearners.filter(
      (l) => l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q),
    );
  }, [availableLearners, learnerSearch]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  // ── Stats ────────────────────────────────────────────────────
  const totalMembers = groups.reduce((s, g) => s + g.membersCount, 0);
  const emptyGroups = groups.filter((g) => g.membersCount === 0).length;

  // ── Actions ──────────────────────────────────────────────────
  const openCreate = () => { setEditId(null); setEditForm({ name: '', description: '' }); setModalOpen(true); };
  const openEdit = (g: GroupSummary) => { setEditId(g.id); setEditForm({ name: g.name, description: g.description ?? '' }); setModalOpen(true); };

  const saveGroup = async () => {
    if (!editForm.name.trim()) return;
    try {
      setSaving(true);
      if (editId) {
        await api.put(`/institution/groups/${editId}`, { name: editForm.name, description: editForm.description });
      } else {
        await api.post('/institution/groups', { name: editForm.name, description: editForm.description });
      }
      setModalOpen(false);
      await fetchGroups();
    } catch { /* keep modal open */ } finally { setSaving(false); }
  };

  const deleteGroup = async (id: string) => {
    try {
      setDeletingId(id);
      await api.delete(`/institution/groups/${id}`);
      setGroups((prev) => prev.filter((g) => g.id !== id));
      if (selectedGroupId === id) { setSelectedGroupId(null); setGroupDetail(null); }
    } catch { /* silent */ } finally { setDeletingId(null); }
  };

  const openAssignPanel = () => {
    if (!selectedGroupId) return;
    setLearnerSearch('');
    setPanelOpen(true);
    fetchAvailableLearners(selectedGroupId);
  };

  const assignLearner = async (learner: AvailableLearner) => {
    if (!selectedGroupId) return;
    try {
      setAssigningId(learner.id);
      await api.post(`/institution/groups/${selectedGroupId}/members`, { userIds: [learner.id], role: 'member' });
      setAvailableLearners((prev) => prev.filter((l) => l.id !== learner.id));
      await fetchGroupDetail(selectedGroupId);
      setGroups((prev) => prev.map((g) => (g.id === selectedGroupId ? { ...g, membersCount: g.membersCount + 1 } : g)));
    } catch { /* silent */ } finally { setAssigningId(null); }
  };

  const removeMember = async (participantId: string) => {
    if (!selectedGroupId) return;
    try {
      setRemovingMemberId(participantId);
      await api.delete(`/institution/groups/${selectedGroupId}/members/${participantId}`);
      await fetchGroupDetail(selectedGroupId);
      setGroups((prev) => prev.map((g) => (g.id === selectedGroupId ? { ...g, membersCount: Math.max(0, g.membersCount - 1) } : g)));
    } catch { /* silent */ } finally { setRemovingMemberId(null); }
  };

  // ── Detail view ──────────────────────────────────────────────
  if (selectedGroupId) {
    return (
      <div className="relative flex min-h-0 flex-1">
        {/* Main content */}
        <div className={cn('flex-1 space-y-5 p-6 transition-all', panelOpen && 'me-80')}>
          <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => { setSelectedGroupId(null); setPanelOpen(false); }} size="sm">
            {t('institution.groups.backList')}
          </Button>

          {detailLoading && !groupDetail ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#5B6472]/70" />
            </div>
          ) : groupDetail ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{groupDetail.name}</h1>
                  {groupDetail.description && (
                    <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{groupDetail.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" leftIcon={<Edit2 className="h-4 w-4" />} size="sm" onClick={() => openEdit({ ...groupDetail, membersCount: groupDetail.members.length })}>
                    {t('institution.common.edit')}
                  </Button>
                  <Button variant="primary" leftIcon={<UserPlus className="h-4 w-4" />} size="sm" onClick={openAssignPanel}>
                    {t('institution.groups.addLearner')}
                  </Button>
                </div>
              </div>

              {/* Members stats */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex items-center gap-4 rounded-xl border border-[#E8EDF5] bg-white p-4 dark:border-[#19335E] dark:bg-[#122644]">
                  <div className="rounded-lg bg-blue-50 p-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{groupDetail.members.length}</p>
                    <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.members')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-xl border border-[#E8EDF5] bg-white p-4 dark:border-[#19335E] dark:bg-[#122644]">
                  <div className="rounded-lg bg-[#F0F4FA] p-2 text-[#19335E] dark:bg-[#061128]/30 dark:text-[#5C82C5]">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">
                      {groupDetail.createdAt ? new Date(groupDetail.createdAt).toLocaleDateString(dateLocale) : t('institution.common.empty')}
                    </p>
                    <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.groups.dateCreated')}</p>
                  </div>
                </div>
              </div>

              {/* Members table */}
              <Table striped hoverable>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('institution.groups.colMember')}</TableHead>
                    <TableHead>{t('institution.common.email')}</TableHead>
                    <TableHead>{t('institution.groups.colAdded')}</TableHead>
                    <TableHead className="!text-end whitespace-nowrap ps-2 pe-4">{t('institution.common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupDetail.members.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-[#5B6472]/70">
                        {t('institution.groups.noMembers')}
                      </TableCell>
                    </TableRow>
                  ) : groupDetail.members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar name={m.name} size="sm" />
                          <span className="font-medium text-[#0C1C38] dark:text-white">{m.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#5B6472] dark:text-[#B6CAEC]">{m.email}</TableCell>
                      <TableCell className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                        {new Date(m.joinedAt).toLocaleDateString(dateLocale)}
                      </TableCell>
                      <TableCell className="text-end align-middle ps-2 pe-4">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeMember(m.id)}
                            disabled={removingMemberId === m.id}
                            className="rounded-lg p-1.5 text-[#5B6472]/70 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                            title={t('institution.groups.removeFromGroup')}
                            aria-label={t('institution.groups.removeFromGroup')}
                          >
                            {removingMemberId === m.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <UserMinus className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <div className="flex flex-col items-center py-16 text-[#5B6472]/70">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{t('institution.groups.loadGroupError')}</p>
            </div>
          )}
        </div>

        {/* Right panel — assign learners */}
        <AnimatePresence>
          {panelOpen && (
            <motion.div
              initial={{ x: 320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 320, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed end-0 top-0 z-40 flex h-full w-80 flex-col border-s border-[#E8EDF5] bg-white shadow-xl dark:border-[#19335E] dark:bg-[#0C1C38]"
            >
              <div className="flex items-center justify-between border-b border-[#E8EDF5] px-4 py-3 dark:border-[#19335E]">
                <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white">{t('institution.groups.panelTitle')}</h3>
                <button
                  type="button"
                  onClick={() => setPanelOpen(false)}
                  className="rounded-lg p-1 text-[#5B6472]/70 transition hover:bg-[#F0F4FA] hover:text-[#5B6472] dark:hover:bg-[#122644]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="border-b border-[#E8EDF5] px-4 py-3 dark:border-[#19335E]">
                <Input
                  placeholder={t('institution.common.search')}
                  value={learnerSearch}
                  onChange={(e) => setLearnerSearch(e.target.value)}
                  leftIcon={<Search className="h-4 w-4" />}
                  inputSize="sm"
                />
              </div>

              <div className="flex-1 overflow-y-auto">
                {learnersLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-[#5B6472]/70" />
                  </div>
                ) : filteredLearners.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-[#5B6472]/70">
                    {availableLearners.length === 0
                      ? t('institution.groups.noLearnersAvailable')
                      : t('institution.groups.noResults')}
                  </div>
                ) : (
                  <ul className="divide-y divide-[#F0F4FA] dark:divide-[#122644]">
                    {filteredLearners.map((l) => (
                      <li key={l.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar name={l.name} size="sm" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0C1C38] dark:text-white truncate">{l.name}</p>
                            <p className="text-xs text-[#5B6472]/70 truncate">{l.email}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => assignLearner(l)}
                          disabled={assigningId === l.id}
                          className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-[#0C1C38] px-2.5 py-1 text-xs font-medium text-white transition hover:bg-[#19335E] disabled:opacity-50"
                        >
                          {assigningId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                          {t('institution.common.add')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit modal (reused from list) */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
          <ModalHeader>{t('institution.groups.modalEdit')}</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input label={t('institution.groups.groupName')} value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} autoFocus />
              <Input label={t('institution.common.description')} value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>{t('institution.common.cancel')}</Button>
            <Button variant="primary" onClick={saveGroup} disabled={!editForm.name.trim() || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('institution.common.save')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────
  return (
    <div className="space-y-5 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.groups.title')}</h1>
          <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
            {t('institution.groups.subtitle', { count: groups.length })}
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} size="sm" onClick={openCreate}>
          {t('institution.groups.create')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: t('institution.groups.statGroups'), value: groups.length, icon: FolderOpen, color: 'text-blue-600 dark:text-blue-400' },
          { label: t('institution.groups.statTotalMembers'), value: totalMembers, icon: Users, color: 'text-[#19335E] dark:text-[#5C82C5]' },
          { label: t('institution.groups.statEmpty'), value: emptyGroups, icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400' },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-4 rounded-xl border border-[#E8EDF5] bg-white p-4 dark:border-[#19335E] dark:bg-[#122644]">
            <div className={cn('rounded-lg bg-[#F8FAFC] p-2 dark:bg-[#19335E]/50', s.color)}>
              <s.icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{s.value}</p>
              <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={fetchGroups} className="ms-auto">{t('institution.common.retry')}</Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            placeholder={t('institution.groups.searchPh')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            leftIcon={<Search className="h-4 w-4" />}
            inputSize="sm"
          />
        </div>
        <Button variant="ghost" size="icon" onClick={fetchGroups} title={t('institution.common.refresh')}>
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Table */}
      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead sortable sortDirection={sortField === 'name' ? sortDir : undefined} onSort={() => handleSort('name')}>
              {t('institution.groups.colGroup')}
            </TableHead>
            <TableHead>{t('institution.common.description')}</TableHead>
            <TableHead sortable sortDirection={sortField === 'membersCount' ? sortDir : undefined} onSort={() => handleSort('membersCount')}>
              {t('institution.common.members')}
            </TableHead>
            <TableHead sortable sortDirection={sortField === 'createdAt' ? sortDir : undefined} onSort={() => handleSort('createdAt')}>
              {t('institution.groups.colCreated')}
            </TableHead>
            <TableHead className="!text-end whitespace-nowrap ps-2 pe-4">
              {t('institution.common.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center text-[#5B6472]/70">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </TableCell>
            </TableRow>
          ) : paginated.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center text-[#5B6472]/70">
                {groups.length === 0
                  ? t('institution.groups.emptyNone')
                  : t('institution.groups.emptyFilter')}
              </TableCell>
            </TableRow>
          ) : paginated.map((g) => (
            <TableRow key={g.id} className="cursor-pointer" onClick={() => setSelectedGroupId(g.id)}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0C1C38]/10 text-[#0C1C38]">
                    <Users className="h-4 w-4" />
                  </div>
                  <span className="font-medium text-[#0C1C38] dark:text-white">{g.name}</span>
                </div>
              </TableCell>
              <TableCell className="max-w-xs truncate text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                {g.description || t('institution.common.empty')}
              </TableCell>
              <TableCell>
                <Badge variant={g.membersCount > 0 ? 'info' : 'default'}>
                  {t('institution.groups.memberCount', { count: g.membersCount })}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                {g.createdAt ? new Date(g.createdAt).toLocaleDateString(dateLocale) : t('institution.common.empty')}
              </TableCell>
              <TableCell className="text-end align-middle ps-2 pe-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedGroupId(g.id)}
                    title={t(
                      'institution.groups.actionManageMembers',
                      'Voir le groupe et ajouter ou retirer des apprenants',
                    )}
                    aria-label={t(
                      'institution.groups.actionManageMembers',
                      'Voir le groupe et ajouter ou retirer des apprenants',
                    )}
                  >
                    <UserRoundCog className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(g)} title={t('institution.common.edit')}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteGroup(g.id)} disabled={deletingId === g.id} title={t('institution.common.delete')}>
                    {deletingId === g.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-red-500" />}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-[#5B6472] dark:text-[#B6CAEC]">
          <span>{t('institution.groups.countShown', { count: filtered.length })}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>{t('institution.common.page')} {page} {t('institution.common.of')} {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader>{editId ? t('institution.groups.modalEdit') : t('institution.groups.modalCreate')}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label={t('institution.groups.groupName')} value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} autoFocus />
            <Input label={t('institution.common.description')} value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>{t('institution.common.cancel')}</Button>
          <Button variant="primary" onClick={saveGroup} disabled={!editForm.name.trim() || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('institution.common.save')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
